import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Avatar, Box, Chip, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { NotFound } from "../NotFound";
import { fetchProviderProfile, ProviderProfileNotFoundError } from "../../utils/fetchProviderProfile";
import { PublicProviderProfile } from "../../types/providerProfile";
import { getProviderDisplayCredentials, getProviderDisplayName } from "../../utils/providerDisplay";

const TEAL = "#008080";
const MINT_BG = "#F4F9F8";

type LoadState =
  | { status: "loading" }
  | { status: "notFound" }
  | { status: "error"; message: string }
  | { status: "ready"; profile: PublicProviderProfile };

// A `<meta name="robots" content="noindex">` tag tells search engines not to list a page
// in search results. Provider profiles should only be reachable via a direct link (email,
// result screen), not surfaced by search — so we add the tag only while this route is
// mounted, since this SPA serves every route from the same static index.html and a tag
// placed there would noindex the intake form too.
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
}

export function ProviderProfile() {
  const { uuid } = useParams<{ uuid: string }>();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useNoIndex();

  useEffect(() => {
    if (!uuid) {
      setState({ status: "notFound" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    fetchProviderProfile(uuid)
      .then((profile) => {
        if (!cancelled) setState({ status: "ready", profile });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ProviderProfileNotFoundError) {
          setState({ status: "notFound" });
          return;
        }
        const message = error instanceof Error ? error.message : "Something went wrong";
        setState({ status: "error", message });
      });

    return () => {
      cancelled = true;
    };
  }, [uuid]);

  if (state.status === "loading") {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: MINT_BG }}>
        <Stack alignItems="center" py={8}>
          <CircularProgress size={48} sx={{ color: TEAL }} />
        </Stack>
      </Box>
    );
  }

  if (state.status === "notFound") {
    return (
      <NotFound
        hideHeader
        title="Provider not found"
        message="This provider's profile isn't available. They may no longer be accepting new clients."
      />
    );
  }

  if (state.status === "error") {
    return <NotFound hideHeader title="Something went wrong" message={state.message} />;
  }

  return <ProviderProfileCard profile={state.profile} />;
}

function ProviderProfileCard({ profile }: { profile: PublicProviderProfile }) {
  const name = getProviderDisplayName(profile);
  const credentials = getProviderDisplayCredentials(
    profile.professionalCredentials,
    profile.otherProfessionalCredentials
  );

  const hasContactInfo = !!(profile.website || profile.phone || profile.email || profile.state);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: MINT_BG }}>
      <Box sx={{ maxWidth: 860, mx: "auto", py: { xs: 3, md: 4 }, px: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            bgcolor: "#fff",
            border: "1px solid #E0E0E0",
            borderRadius: 2,
            p: 3,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "center", sm: "flex-start" }}>
            <Avatar
              src={profile.avatarThumb || profile.avatar || undefined}
              alt={name}
              sx={{
                width: 128,
                height: 128,
                bgcolor: "#CFD8DC",
                flexShrink: 0,
                fontSize: "2rem",
                "& img": { objectFit: "cover" },
              }}
            >
              {name ? name[0] : "?"}
            </Avatar>

            <Stack spacing={0.5} textAlign={{ xs: "center", sm: "left" }}>
              <Typography variant="h5" fontWeight={700} color="#212121">
                {name}
              </Typography>
              {credentials && (
                <Typography variant="body2" sx={{ color: TEAL, fontWeight: 600 }}>
                  {credentials}
                </Typography>
              )}
              {profile.organizationName && (
                <Typography variant="body2" color="#616161">
                  {profile.organizationName}
                </Typography>
              )}
            </Stack>
          </Stack>

          {hasContactInfo && (
            <Stack spacing={1} mt={3}>
              {profile.website && (
                <ContactRow icon={<LanguageOutlinedIcon />} href={profile.website} label={profile.website} />
              )}
              {profile.phone && (
                <ContactRow icon={<PhoneOutlinedIcon />} href={`tel:${profile.phone}`} label={profile.phone} />
              )}
              {profile.email && (
                <ContactRow icon={<MailOutlineIcon />} href={`mailto:${profile.email}`} label={profile.email} />
              )}
              {profile.state && <ContactRow icon={<PlaceOutlinedIcon />} label={profile.state} />}
            </Stack>
          )}

          <ProfileSection label="About">
            {profile.bio && (
              <Typography variant="body2" color="#616161" sx={{ lineHeight: 1.7 }}>
                {profile.bio}
              </Typography>
            )}
          </ProfileSection>

          <ChipSection label="Certifications" items={profile.certifications} />
          <ChipSection label="Specialties" items={profile.clinicalSpecialties} />
          <ChipSection label="Modalities" items={profile.modalities} />
          <ChipSection label="Delivery Options" items={profile.deliveryModels} />
          <ChipSection label="Languages" items={profile.languagesSpoken} />
          <ChipSection label="Age Groups" items={profile.population} />
        </Box>
      </Box>
    </Box>
  );
}

function ContactRow({ icon, href, label }: { icon: React.ReactNode; href?: string; label: string }) {
  const content = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box sx={{ color: "#757575", display: "flex", "& svg": { fontSize: 18 } }}>{icon}</Box>
      <Typography variant="body2" color="#616161">
        {label}
      </Typography>
    </Stack>
  );

  if (!href) return content;

  return (
    <a href={href} style={{ textDecoration: "none", color: "inherit" }}>
      {content}
    </a>
  );
}

function ProfileSection({ label, children }: { label: string; children: React.ReactNode }) {
  const hasContent = Array.isArray(children) ? children.some(Boolean) : !!children;

  if (!hasContent) return null;

  return (
    <>
      <Divider sx={{ my: 3 }} />
      <Box>
        <Typography variant="overline" sx={{ color: "#8A8A8A", fontWeight: 600, letterSpacing: 1 }}>
          {label}
        </Typography>
        <Box mt={1}>{children}</Box>
      </Box>
    </>
  );
}

function ChipSection({ label, items }: { label: string; items?: string[] }) {
  if (!items || items.length === 0) return null;

  return (
    <ProfileSection label={label}>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {items.map((item) => (
          <Chip key={item} label={item} size="small" sx={{ bgcolor: "#F0F0F0", color: "#424242", borderRadius: 1 }} />
        ))}
      </Stack>
    </ProfileSection>
  );
}
